import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import {
  Activity,
  FileText,
  Brain,
  BarChart3,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';

// Importar hooks
import { useDashboard } from '../hooks';

export default function MCPBackoffice() {
  // Hooks para datos
  const dashboard = useDashboard();

  // Componente de error
  const ErrorAlert = ({
    error,
    onRetry,
  }: {
    error: any;
    onRetry: () => void;
  }) => (
    <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-4'>
      <div className='flex items-center'>
        <AlertCircle className='h-4 w-4 text-red-600 mr-2' />
        <span className='text-red-800'>
          {error?.message || 'Ha ocurrido un error'}
        </span>
        <Button
          variant='outline'
          size='sm'
          onClick={onRetry}
          className='ml-auto'
        >
          <RefreshCw className='h-3 w-3 mr-1' />
          Reintentar
        </Button>
      </div>
    </div>
  );

  return (
    <div className='flex-1 space-y-6 p-6'>
      {/* Manejo de errores globales */}
      {dashboard.error && (
        <ErrorAlert error={dashboard.error} onRetry={dashboard.refreshStats} />
      )}

      {/* Tarjetas de estadísticas principales */}
      <div className='grid gap-4 md:grid-cols-4'>
        <Card className='border-0 shadow-sm card-hover'>
          <CardContent className='p-6'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg animate-float'>
                <Activity className='h-6 w-6 text-blue-600' />
              </div>
              <div className='flex-1'>
                <p className='text-sm font-medium text-gray-600'>
                  Evaluaciones
                </p>
                <p className='text-2xl font-bold text-gray-900'>
                  {dashboard.loading
                    ? '...'
                    : dashboard.stats?.evaluations?.total || 0}
                </p>
                <div className='flex items-center space-x-2 mt-1'>
                  <span className='text-xs text-green-600 font-medium'>
                    ✓ {dashboard.stats?.evaluations?.completed || 0}
                  </span>
                  {(dashboard.stats?.evaluations?.failed || 0) > 0 && (
                    <span className='text-xs text-red-600 font-medium'>
                      ✗ {dashboard.stats?.evaluations?.failed}
                    </span>
                  )}
                  {(dashboard.stats?.evaluations?.pending || 0) > 0 && (
                    <span className='text-xs text-yellow-600 font-medium'>
                      ⏳ {dashboard.stats?.evaluations?.pending}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-sm card-hover'>
          <CardContent className='p-6'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg animate-float'>
                <FileText className='h-6 w-6 text-green-600' />
              </div>
              <div className='flex-1'>
                <p className='text-sm font-medium text-gray-600'>Tasks</p>
                <p className='text-2xl font-bold text-gray-900'>
                  {dashboard.loading
                    ? '...'
                    : dashboard.stats?.tasks?.total || 0}
                </p>
                <div className='flex items-center space-x-2 mt-1'>
                  <span className='text-xs text-green-600 font-medium'>
                    ▶ {dashboard.stats?.tasks?.active || 0} activas
                  </span>
                  <span className='text-xs text-blue-600 font-medium'>
                    ✓ {dashboard.stats?.tasks?.completed || 0}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-sm card-hover'>
          <CardContent className='p-6'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg animate-float'>
                <Brain className='h-6 w-6 text-purple-600' />
              </div>
              <div className='flex-1'>
                <p className='text-sm font-medium text-gray-600'>Modelos</p>
                <p className='text-2xl font-bold text-gray-900'>
                  {dashboard.loading
                    ? '...'
                    : dashboard.stats?.models?.total || 0}
                </p>
                <div className='flex items-center space-x-2 mt-1'>
                  <span className='text-xs text-green-600 font-medium'>
                    🚀 {dashboard.stats?.models?.deployed || 0}
                  </span>
                  {(dashboard.stats?.models?.training || 0) > 0 && (
                    <span className='text-xs text-purple-600 font-medium'>
                      ⚡ {dashboard.stats?.models?.training}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-sm card-hover'>
          <CardContent className='p-6'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg animate-float'>
                <BarChart3 className='h-6 w-6 text-orange-600' />
              </div>
              <div className='flex-1'>
                <p className='text-sm font-medium text-gray-600'>
                  Score Promedio
                </p>
                <p className='text-2xl font-bold text-gray-900 gradient-text'>
                  {dashboard.loading
                    ? '...'
                    : dashboard.stats?.evaluations?.averageScore?.toFixed(1) ||
                      '0.0'}
                  /10
                </p>
                <div className='mt-1'>
                  <span className='text-xs text-gray-500'>
                    Última evaluación
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enlaces rápidos a las secciones */}
      <div className='grid gap-4 md:grid-cols-3'>
        <Card
          className='border-0 shadow-sm card-hover cursor-pointer transition-all duration-200 hover:scale-105'
          onClick={() => (window.location.href = '/evaluations')}
        >
          <CardHeader className='pb-4'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg'>
                <Activity className='h-6 w-6 text-blue-600' />
              </div>
              <div>
                <CardTitle className='text-lg text-gray-900'>
                  Evaluaciones
                </CardTitle>
                <CardDescription>
                  Gestionar evaluaciones del sistema
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card
          className='border-0 shadow-sm card-hover cursor-pointer transition-all duration-200 hover:scale-105'
          onClick={() => (window.location.href = '/tasks')}
        >
          <CardHeader className='pb-4'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg'>
                <FileText className='h-6 w-6 text-green-600' />
              </div>
              <div>
                <CardTitle className='text-lg text-gray-900'>Tasks</CardTitle>
                <CardDescription>Administrar tareas y rúbricas</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        <Card
          className='border-0 shadow-sm card-hover cursor-pointer transition-all duration-200 hover:scale-105'
          onClick={() => (window.location.href = '/models')}
        >
          <CardHeader className='pb-4'>
            <div className='flex items-center space-x-3'>
              <div className='flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg'>
                <Brain className='h-6 w-6 text-purple-600' />
              </div>
              <div>
                <CardTitle className='text-lg text-gray-900'>Modelos</CardTitle>
                <CardDescription>Ver y gestionar modelos de IA</CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    </div>
  );
}
